import * as XLSX from 'xlsx';

/**
 * Supported file types for attendance import
 */
export const SUPPORTED_FILE_TYPES = {
  EXCEL: ['xlsx', 'xls'],
  CSV: ['csv'],
  ALL: ['xlsx', 'xls', 'csv']
};

/**
 * Parse attendance file (Excel or CSV) and extract attendee data
 * @param {File} file - The uploaded file
 * @returns {Promise<{data: Array, metadata: Object}>} Parsed data and metadata
 */
export async function parseAttendanceFile(file) {
  return new Promise((resolve, reject) => {
    const extension = file.name.split('.').pop().toLowerCase();
    
    if (!SUPPORTED_FILE_TYPES.ALL.includes(extension)) {
      reject(new Error(`Unsupported file type: ${extension}`));
      return;
    }

    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        let workbook;
        
        if (SUPPORTED_FILE_TYPES.EXCEL.includes(extension)) {
          // Parse Excel file
          const data = new Uint8Array(e.target.result);
          workbook = XLSX.read(data, { type: 'array' });
        } else if (extension === 'csv') {
          // Parse CSV file
          const text = e.target.result;
          workbook = XLSX.read(text, { type: 'string' });
        }

        // Get the first worksheet
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        
        // Convert to JSON with header row
        const jsonData = XLSX.utils.sheet_to_json(worksheet, {
          header: 1, // Return array of arrays
          defval: '', // Default value for empty cells
          blankrows: false // Skip completely blank rows
        });

        if (jsonData.length < 2) {
          reject(new Error('File must contain at least a header row and one data row'));
          return;
        }

        // Extract headers and data
        const headers = jsonData[0].map(h => String(h).trim());
        const rows = jsonData.slice(1).map((row, index) => ({
          rowNumber: index + 2, // +2 because we skip header and arrays are 0-indexed
          data: row,
          originalRow: row.slice() // Keep original for debugging
        }));

        const metadata = {
          fileName: file.name,
          fileSize: file.size,
          extension,
          sheetName,
          totalRows: rows.length,
          headers,
          parsedAt: new Date().toISOString()
        };

        resolve({
          data: { headers, rows },
          metadata
        });
      } catch (error) {
        reject(new Error(`Failed to parse file: ${error.message}`));
      }
    };

    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };

    // Read file based on type
    if (SUPPORTED_FILE_TYPES.EXCEL.includes(extension)) {
      reader.readAsArrayBuffer(file);
    } else {
      reader.readAsText(file);
    }
  });
}

/**
 * Analyze CSV columns to identify session attendance columns
 * @param {Object} data - Parsed data with headers and rows
 * @param {Array} sessions - Array of session objects from Airtable
 * @returns {Object} Session mapping configuration
 */
export function analyzeSessionColumns(data, sessions) {
  const sessionMapping = {
    sessionColumns: {},
    unmappedColumns: [],
    detectedSessions: []
  };

  // Auto-map all sessions from the SO (no need to find columns in CSV)
  // All attendees will be enrolled in all sessions by default
  sessions.forEach(session => {
    const sessionNum = session.sessionNo;
    sessionMapping.sessionColumns[sessionNum] = {
      csvColumn: null, // No CSV column needed - enrollment is automatic
      csvIndex: -1,    // No CSV column needed
      sessionId: session.id,
      sessionName: session.sessionName,
      attendeeCount: 0 // Will be calculated during processing
    };
    sessionMapping.detectedSessions.push(sessionNum);
  });

  return sessionMapping;
}

/**
 * Transform parsed CSV data to attendance format
 * @param {Object} data - Parsed CSV data
 * @param {Object} sessionMapping - Session column mapping
 * @param {Object} options - Additional options (soNumber, soDetails, fileName)
 * @returns {Object} Transformed attendance data
 */
export function transformToAttendanceFormat(data, sessionMapping, options = {}) {
  const { headers, rows } = data;
  const attendees = [];
  const warnings = [];

  // Find required column indices
  const firstNameIndex = findColumnIndex(headers, ['first name', 'firstname', 'first', 'given name']);
  const lastNameIndex = findColumnIndex(headers, ['last name', 'lastname', 'last', 'surname', 'family name']);
  const fullNameIndex = findColumnIndex(headers, ['full name', 'fullname', 'name']);
  const emailIndex = findColumnIndex(headers, ['email', 'email address', 'e-mail']);
  const companyIndex = findColumnIndex(headers, ['client', 'company', 'client name', 'company name', 'organization', 'employer']);

  // Warn if no name columns found
  if (firstNameIndex === -1 && lastNameIndex === -1 && fullNameIndex === -1) {
    warnings.push('No name columns found - using default values');
  } else if (firstNameIndex === -1 && lastNameIndex === -1 && fullNameIndex >= 0) {
    warnings.push('Using Full Name column - will split into first and last names');
  }

  if (emailIndex === -1) warnings.push('Email column not found - this will prevent proper attendee identification');

  // Process each row
  rows.forEach(row => {
    // Extract company value and convert empty strings to null, apply title case
    let companyValue = null;
    if (companyIndex >= 0) {
      const rawCompany = (row.data[companyIndex] || '').toString().trim();
      companyValue = rawCompany.length > 0 ? toTitleCase(rawCompany) : null;
    }

    // Determine first/last name - check for Full Name column first
    let firstName = 'Unknown';
    let lastName = 'User';

    if (firstNameIndex >= 0 && lastNameIndex >= 0) {
      // Both first and last name columns exist - use them
      firstName = toTitleCase((row.data[firstNameIndex] || '').toString().trim()) || 'Unknown';
      lastName = toTitleCase((row.data[lastNameIndex] || '').toString().trim()) || 'User';
    } else if (fullNameIndex >= 0) {
      // Full name column exists - split it
      const fullName = (row.data[fullNameIndex] || '').toString().trim();
      const split = splitFullName(fullName);
      firstName = toTitleCase(split.firstName) || 'Unknown';
      lastName = toTitleCase(split.lastName) || 'User';
    } else if (firstNameIndex >= 0) {
      // Only first name column exists
      firstName = toTitleCase((row.data[firstNameIndex] || '').toString().trim()) || 'Unknown';
      lastName = 'User';
    } else if (lastNameIndex >= 0) {
      // Only last name column exists
      firstName = 'Unknown';
      lastName = toTitleCase((row.data[lastNameIndex] || '').toString().trim()) || 'User';
    }

    const attendee = {
      rowNumber: row.rowNumber,
      firstName,
      lastName,
      email: emailIndex >= 0 ? (row.data[emailIndex] || '').toString().trim() : '',
      companyFromCSV: companyValue,
      sessionAttendance: {},
      originalRow: row.originalRow
    };

    // Skip rows with no meaningful data
    if (!attendee.firstName && !attendee.lastName && !attendee.email) {
      return;
    }

    // Auto-enroll in all sessions for this SO (default to not present)
    if (options.sessions && options.sessions.length > 0) {
      options.sessions.forEach(session => {
        // Default all attendees to not present - they can be marked present later
        attendee.sessionAttendance[session.sessionNo] = false;
      });
    }

    attendees.push(attendee);
  });

  return {
    attendees,
    sessionMapping,
    metadata: {
      soNumber: options.soNumber,
      soDetails: options.soDetails,
      fileName: options.fileName,
      totalAttendees: attendees.length,
      processedAt: new Date().toISOString()
    },
    warnings
  };
}

/**
 * Find column index by matching against possible names
 * @param {Array} headers - Array of header names
 * @param {Array} possibleNames - Array of possible column names to match
 * @returns {number} Column index or -1 if not found
 */
function findColumnIndex(headers, possibleNames) {
  for (let i = 0; i < headers.length; i++) {
    const header = headers[i].toLowerCase().trim();
    if (possibleNames.some(name => header === name || header.includes(name))) {
      return i;
    }
  }
  return -1;
}

/**
 * Convert string to title case
 * @param {string} str - String to convert
 * @returns {string} Title cased string
 */
function toTitleCase(str) {
  if (!str || typeof str !== 'string') return str;

  return str
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Parse attendance value from CSV cell
 * @param {any} value - Cell value from CSV
 * @returns {boolean} Whether the attendee is marked as present
 */
function parseAttendanceValue(value) {
  if (value === null || value === undefined) return false;
  
  const stringValue = value.toString().toLowerCase().trim();
  
  // Empty values = not present
  if (!stringValue) return false;
  
  // Common "present" values
  const presentValues = ['checked', 'present', 'attended', 'yes', 'y', '1', 'true', 'x', '✓'];
  const absentValues = ['absent', 'no', 'n', '0', 'false', ''];
  
  if (presentValues.includes(stringValue)) return true;
  if (absentValues.includes(stringValue)) return false;
  
  // For numeric values, anything > 0 is considered present
  const numericValue = parseFloat(stringValue);
  if (!isNaN(numericValue)) {
    return numericValue > 0;
  }
  
  // Default: if there's any non-empty content, consider it present
  return true;
}

/**
 * Validate attendance data before import
 * @param {Object} attendanceData - Transformed attendance data
 * @param {Object} importData - Import configuration
 * @returns {Object} Validation results
 */
export function validateAttendanceData(attendanceData, importData) {
  const errors = [];
  const warnings = [];
  
  // Check basic requirements
  if (!importData.soNumber) {
    errors.push('SO Number is required');
  }
  
  if (!importData.soDetails) {
    errors.push('SO details must be loaded');
  }
  
  if (!attendanceData.attendees || attendanceData.attendees.length === 0) {
    errors.push('No attendees found in the file');
  }
  
  // Validate attendee data
  let validAttendees = 0;
  let attendeesWithoutEmail = 0;
  
  attendanceData.attendees.forEach(attendee => {
    if (!attendee.email) {
      attendeesWithoutEmail++;
    } else {
      // Basic email validation
      if (!isValidEmail(attendee.email)) {
        warnings.push(`Invalid email format: ${attendee.email} (Row ${attendee.rowNumber})`);
      } else {
        validAttendees++;
      }
    }
    
    if (!attendee.firstName && !attendee.lastName) {
      warnings.push(`Missing name information for row ${attendee.rowNumber}`);
    }
  });
  
  if (attendeesWithoutEmail > 0) {
    warnings.push(`${attendeesWithoutEmail} attendees without email addresses - these will be harder to track`);
  }
  
  // Validate session mapping
  if (!attendanceData.sessionMapping || Object.keys(attendanceData.sessionMapping.sessionColumns).length === 0) {
    warnings.push('No session attendance columns detected - attendees will be created but no attendance will be recorded');
  }
  
  // Check if sessions exist
  if (importData.sessions && importData.sessions.length === 0) {
    warnings.push('No sessions found for this SO - attendance data cannot be imported');
  }
  
  const isValid = errors.length === 0;
  
  return {
    isValid,
    errors,
    warnings,
    summary: {
      totalAttendees: attendanceData.attendees.length,
      validAttendees,
      attendeesWithoutEmail,
      sessionsMapped: Object.keys(attendanceData.sessionMapping.sessionColumns).length
    }
  };
}

/**
 * Simple email validation
 * @param {string} email - Email to validate
 * @returns {boolean} Whether email is valid
 */
function isValidEmail(email) {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

/**
 * Generate preview data for the UI
 * @param {Object} attendanceData - Transformed attendance data
 * @param {number} maxRows - Maximum number of rows to include in preview
 * @returns {Object} Preview data
 */
export function generateAttendancePreview(attendanceData, maxRows = 10) {
  const preview = {
    attendees: attendanceData.attendees.slice(0, maxRows),
    hasMoreRows: attendanceData.attendees.length > maxRows,
    totalRows: attendanceData.attendees.length,
    sessionMapping: attendanceData.sessionMapping,
    metadata: attendanceData.metadata
  };
  
  return preview;
}

/**
 * Retry operation with exponential backoff
 * @param {Function} operation - Async function to retry
 * @param {number} maxRetries - Maximum number of retry attempts (default: 3)
 * @returns {Promise<any>} Result of the operation
 */
export async function retryWithBackoff(operation, maxRetries = 3) {
  let lastError;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await operation();
    } catch (error) {
      lastError = error;

      if (attempt < maxRetries) {
        // Exponential backoff: 1s, 2s, 4s
        const delayMs = Math.pow(2, attempt) * 1000;
        console.log(`Operation failed (attempt ${attempt + 1}/${maxRetries + 1}), retrying in ${delayMs}ms...`, error.message);
        await new Promise(resolve => setTimeout(resolve, delayMs));
      }
    }
  }

  throw new Error(`Operation failed after ${maxRetries + 1} attempts: ${lastError.message}`);
}

/**
 * Detect client/company column in parsed data
 * @param {Object} parsedData - Parsed CSV data with headers and rows
 * @returns {string | null} - Header name of detected client column, or null if not found
 */
export function detectClientColumn(parsedData) {
  if (!parsedData || !parsedData.headers) return null;

  const { headers } = parsedData;

  // Pattern match headers (case-insensitive)
  const clientPatterns = [
    'client',
    'company',
    'client name',
    'company name',
    'organization',
    'employer'
  ];

  for (const header of headers) {
    const normalizedHeader = header.toLowerCase().trim();
    if (clientPatterns.some(pattern => normalizedHeader === pattern || normalizedHeader.includes(pattern))) {
      return header; // Return original header (preserves case)
    }
  }

  return null;
}

/**
 * Detect duplicate emails in parsed data
 * @param {Array<Object>} parsedData - Array of row objects with Email field
 * @returns {Array<Object>} - Array of duplicate email groups
 */
export function detectDuplicateEmails(parsedData) {
  const emailGroups = new Map();

  parsedData.forEach((row, index) => {
    const email = row.email?.trim().toLowerCase();
    if (!email) return;

    if (!emailGroups.has(email)) {
      emailGroups.set(email, []);
    }

    emailGroups.get(email).push({
      rowIndex: index,
      attendeeName: `${row.firstName || ''} ${row.lastName || ''}`.trim(),
      companyFromCSV: row.companyFromCSV || null
    });
  });

  // Filter to only duplicates (count > 1)
  const duplicates = [];
  emailGroups.forEach((rows, email) => {
    if (rows.length > 1) {
      duplicates.push({
        email,
        count: rows.length,
        rows
      });
    }
  });

  return duplicates;
}

/**
 * Validate client assignments for parsed data
 * @param {Array<Object>} parsedData - CSV rows
 * @param {Object} options - { assignmentMode, clientOverride, detectedClientColumn }
 * @returns {Promise<Object>} { matched, unmatched, empty }
 */
export async function validateClients(parsedData, options) {
  const { assignmentMode, clientOverride, detectedClientColumn } = options;

  // Import lookupClientByName here to avoid circular dependency
  const { lookupClientByName } = await import('./airtable.js');

  // If global override mode, skip company validation entirely
  if (assignmentMode === 'global-override') {
    return {
      matched: [],
      unmatched: [],
      empty: parsedData.map((row, index) => ({ rowIndex: index })),
      overrideApplied: true,
      overrideClient: clientOverride
    };
  }

  // Per-row validation
  const results = { matched: [], unmatched: [], empty: [] };
  const lookupCache = new Map(); // Cache to avoid duplicate API calls

  console.log('🔍 validateClients: Starting validation', {
    totalRows: parsedData.length,
    assignmentMode,
    clientOverride,
    detectedClientColumn
  });

  for (const [index, row] of parsedData.entries()) {
    const company = row.companyFromCSV?.trim();

    if (!company) {
      console.log(`🔍 Row ${index}: No company field`);
      results.empty.push({
        rowIndex: index,
        attendeeName: `${row.firstName || ''} ${row.lastName || ''}`.trim(),
        attendeeEmail: row.email
      });
      continue;
    }

    console.log(`🔍 Row ${index}: Validating company "${company}"`);

    // Check cache first
    if (!lookupCache.has(company)) {
      console.log(`🔍 Row ${index}: Cache miss for "${company}" - calling lookupClientByName`);
      // REUSE existing lookupClientByName from airtable.js
      const client = await lookupClientByName(company);
      lookupCache.set(company, client);
      console.log(`🔍 Row ${index}: lookupClientByName result:`, client ? {
        found: true,
        name: client.name,
        id: client.id,
        isPartialMatch: client.isPartialMatch
      } : { found: false });
    } else {
      console.log(`🔍 Row ${index}: Cache hit for "${company}"`);
    }

    const client = lookupCache.get(company);

    if (client) {
      // Match found (exact or partial)
      console.log(`🔍 Row ${index}: ✅ Match found - "${company}" → "${client.name}"`);
      results.matched.push({
        rowIndex: index,
        companyFromCSV: company,
        attendeeName: `${row.firstName || ''} ${row.lastName || ''}`.trim(),
        attendeeEmail: row.email,
        matchedClient: client
      });
    } else {
      // No match found - requires admin selection
      console.log(`🔍 Row ${index}: ❌ No match found for "${company}"`);
      results.unmatched.push({
        rowIndex: index,
        companyFromCSV: company,
        attendeeName: `${row.firstName || ''} ${row.lastName || ''}`.trim(),
        attendeeEmail: row.email
      });
    }
  }

  console.log('🔍 validateClients: Final results', {
    matched: results.matched.length,
    unmatched: results.unmatched.length,
    empty: results.empty.length,
    matchedSample: results.matched.slice(0, 3).map(m => ({
      company: m.companyFromCSV,
      matchedTo: m.matchedClient?.name
    })),
    unmatchedSample: results.unmatched.slice(0, 3).map(u => u.companyFromCSV)
  });

  return results;
}

/**
 * Validate import size does not exceed maximum row limit
 * @param {Array} parsedData - Parsed CSV/XLSX data rows
 * @returns {Object} { isValid: boolean, error?: string, errorType?: string, details?: Object }
 */
export function validateImportSize(parsedData) {
  const MAX_ROWS = 500;
  const rowCount = parsedData.length;

  if (rowCount > MAX_ROWS) {
    return {
      isValid: false,
      error: `Import limit exceeded: ${rowCount} rows (maximum: ${MAX_ROWS} rows)`,
      errorType: 'SIZE_LIMIT_EXCEEDED',
      details: {
        rowCount,
        maxRows: MAX_ROWS,
        excessRows: rowCount - MAX_ROWS
      }
    };
  }

  return { isValid: true };
}

/**
 * Split full name into first and last name components
 * Handles formats: "John Doe", "Doe, John", "John", etc.
 * @param {string} fullName - Complete name (e.g., "John Doe" or "Doe, John")
 * @returns {Object} { firstName, lastName }
 */
export function splitFullName(fullName) {
  if (!fullName || typeof fullName !== 'string') {
    return { firstName: '', lastName: '' };
  }

  const trimmed = fullName.trim();

  // Check for comma-separated format: "Last, First" or "Last, First Middle"
  if (trimmed.includes(',')) {
    const commaParts = trimmed.split(',').map(p => p.trim());

    if (commaParts.length >= 2) {
      // Format: "Doe, John" or "Doe, John Michael"
      return {
        firstName: commaParts[1], // Everything after comma = first name(s)
        lastName: commaParts[0]   // Everything before comma = last name
      };
    }
  }

  // No comma - split on whitespace
  const parts = trimmed.split(/\s+/);

  if (parts.length === 0) {
    return { firstName: '', lastName: '' };
  }

  if (parts.length === 1) {
    return { firstName: parts[0], lastName: '' };
  }

  // First word = first name, rest = last name
  const firstName = parts[0];
  const lastName = parts.slice(1).join(' ');

  return { firstName, lastName };
}

/**
 * Detect potential mismatches between email and attendee name
 * @param {string} firstName - Attendee first name
 * @param {string} lastName - Attendee last name
 * @param {string} email - Email address
 * @returns {Object} { hasMismatch: boolean, confidence: 'high'|'medium'|'low', reason: string }
 */
export function detectEmailNameMismatch(firstName, lastName, email) {
  if (!email || !firstName) {
    return { hasMismatch: false, confidence: 'low', reason: 'Insufficient data' };
  }

  // Extract username from email (before @)
  const emailParts = email.toLowerCase().split('@');
  if (emailParts.length < 2) {
    return { hasMismatch: false, confidence: 'low', reason: 'Invalid email format' };
  }

  const username = emailParts[0];
  const normalizedFirst = firstName.toLowerCase().replace(/[^a-z]/g, '');
  const normalizedLast = lastName ? lastName.toLowerCase().replace(/[^a-z]/g, '') : '';

  // Pattern 1: username should contain first name (e.g., "lisa" in "lisaross@")
  const containsFirst = username.includes(normalizedFirst);

  // Pattern 2: username should contain last name (e.g., "ross" in "lisaross@")
  const containsLast = normalizedLast && username.includes(normalizedLast);

  // Pattern 3: Check for common concatenations
  const fullNameConcat = normalizedFirst + normalizedLast;
  const containsFullName = username.includes(fullNameConcat);

  // Pattern 4: Check for firstname.lastname pattern
  const dotPattern = `${normalizedFirst}.${normalizedLast}`;
  const containsDotPattern = username.includes(dotPattern.replace('.', ''));

  // Mismatch detection
  if (containsFullName || containsDotPattern) {
    // Strong match - exact concatenation found
    return { hasMismatch: false, confidence: 'high', reason: 'Email matches full name' };
  }

  if (containsFirst && containsLast) {
    // Both names present
    return { hasMismatch: false, confidence: 'high', reason: 'Email contains both names' };
  }

  // Check for mismatch when first name matches but last name is missing
  if (containsFirst && !containsLast && normalizedLast) {
    // First name matches but last name is missing - check if username has DIFFERENT last name
    const potentialLastName = username.replace(normalizedFirst, '').replace(/[^a-z]/g, '');
    if (potentialLastName && potentialLastName !== normalizedLast) {
      return {
        hasMismatch: true,
        confidence: 'high',
        reason: `Email username "${username}" contains "${potentialLastName}" but last name is "${lastName}"`
      };
    }
  }

  if (containsFirst || containsLast) {
    // One name present
    return { hasMismatch: false, confidence: 'medium', reason: 'Email contains one name component' };
  }

  // Check for typos using simple similarity comparison
  const editDistance = (s1, s2) => {
    if (s1.length < 3 || s2.length < 3) return 999; // Too short to compare
    let matches = 0;
    for (let i = 0; i < Math.min(s1.length, s2.length); i++) {
      if (s1[i] === s2[i]) matches++;
    }
    return matches / Math.max(s1.length, s2.length);
  };

  const similarity = editDistance(username, normalizedFirst);
  if (similarity > 0.6) {
    // Close match - might be a typo (e.g., "lisarass" vs "lisaross")
    return {
      hasMismatch: true,
      confidence: 'high',
      reason: `Email username "${username}" is similar but not exact match to name "${normalizedFirst}"`
    };
  }

  // No match at all
  return {
    hasMismatch: true,
    confidence: 'medium',
    reason: `Email username "${username}" doesn't contain name components`
  };
}